import React, { useRef, useEffect } from 'react';

const VERT = [
  '#version 300 es',
  'in vec2 position;',
  'void main() { gl_Position = vec4(position, 0.0, 1.0); }'
].join('\n');

const FRAG = [
  '#version 300 es',
  'precision highp float;',
  'out vec4 O;',
  'uniform float T;',
  'uniform vec2 R;',
  '',
  'mat2 rot(float a){float c=cos(a),s=sin(a);return mat2(c,-s,s,c);}',
  '',
  'float sdBox(vec3 p,vec3 b){',
  '  vec3 q=abs(p)-b;',
  '  return length(max(q,0.))+min(max(q.x,max(q.y,q.z)),0.);',
  '}',
  '',
  'vec3 pal(float t){',
  '  vec3 a=vec3(.5,.3,.4),b=vec3(.5,.3,.3),c=vec3(1.,.7,.6),d=vec3(0.,.15,.45);',
  '  return a+b*cos(6.28318*(c*t+d));',
  '}',
  '',
  'void main(){',
  '  vec2 uv=(gl_FragCoord.xy-.5*R)/R.y;',
  '  float t=T*.25;',
  '  vec3 ro=vec3(0,0,4.5),rd=normalize(vec3(uv,-1.5));',
  '  rd.xz*=rot(t*.4); rd.yz*=rot(sin(t*.3)*.15);',
  '  ro.xz*=rot(t*.4); ro.yz*=rot(sin(t*.3)*.15);',
  '  vec3 sz=vec3(.6,1.8,.15);',
  '  float td=0.; vec3 col=vec3(0); float gw=0.;',
  '  for(int i=0;i<80;i++){',
  '    vec3 p=ro+rd*td; float d=sdBox(p,sz); float gd=abs(d);',
  '    if(gd<1.5){',
  '      float I=exp(-gd*2.5);',
  '      float h=dot(p,vec3(.3,.7,.2))*.8+t*.5;',
  '      col+=pal(h)*I*.06; gw+=I*.04;',
  '    }',
  '    if(d<.001){',
  '      vec2 e=vec2(.001,0);',
  '      vec3 n=normalize(vec3(',
  '        sdBox(p+e.xyy,sz)-sdBox(p-e.xyy,sz),',
  '        sdBox(p+e.yxy,sz)-sdBox(p-e.yxy,sz),',
  '        sdBox(p+e.yyx,sz)-sdBox(p-e.yyx,sz)));',
  '      float fr=pow(1.-abs(dot(n,-rd)),3.);',
  '      float sh=dot(p,vec3(.5,1,.3))*1.2+t;',
  '      vec3 sc=pal(sh);',
  '      col+=mix(vec3(.02),sc,fr*.7)*1.5+sc*fr*.3;',
  '      break;',
  '    }',
  '    td+=d*.8; if(td>20.)break;',
  '  }',
  '  col+=gw*vec3(.9,.4,.2)*.8;',
  '  col+=pal(t*.2)*smoothstep(.6,0.,length(uv))*.03;',
  '  col=col/(col+1.); col=pow(col,vec3(.85));',
  '  O=vec4(col,1);',
  '}'
].join('\n');

const PrismMonolith = ({ className }) => {
  const ref = useRef(null);
  const raf = useRef(null);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const gl = c.getContext('webgl2', { alpha: false, antialias: false });
    if (!gl) return;

    const mk = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.error('Shader error:', gl.getShaderInfoLog(sh));
      }
      return sh;
    };

    const vs = mk(gl.VERTEX_SHADER, VERT);
    const fs = mk(gl.FRAGMENT_SHADER, FRAG);
    const pg = gl.createProgram();
    gl.attachShader(pg, vs);
    gl.attachShader(pg, fs);
    gl.linkProgram(pg);

    if (!gl.getProgramParameter(pg, gl.LINK_STATUS)) {
      console.error('Program error:', gl.getProgramInfoLog(pg));
      return;
    }

    gl.useProgram(pg);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const posLoc = gl.getAttribLocation(pg, 'position');
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    const uT = gl.getUniformLocation(pg, 'T');
    const uR = gl.getUniformLocation(pg, 'R');

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio, 1.5);
      c.width = c.clientWidth * dpr;
      c.height = c.clientHeight * dpr;
      gl.viewport(0, 0, c.width, c.height);
      gl.uniform2f(uR, c.width, c.height);
    };

    resize();
    window.addEventListener('resize', resize);

    const startTime = performance.now();
    const loop = () => {
      gl.uniform1f(uT, (performance.now() - startTime) * 0.001);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);

    return () => {
      window.removeEventListener('resize', resize);
      if (raf.current) cancelAnimationFrame(raf.current);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      className={className}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
};

export default PrismMonolith;
